import { computed, makeObservable, action, observable } from 'mobx'
import isArray from 'lodash/isArray'
import upperFirst from 'lodash/upperFirst'
import get from 'lodash/get'

import {
  SECTION_TYPES,
  COLUMNS_COUNT,
  SECTION_LAYOUTS,
  TWOCOL_SECTION_BG_IMAGE_POSITION_TYPES,
  SECTION_VERTICAL_ALIGN,
  SECTION_WIDTH_TYPES,
} from './constants'
import { BlockModel } from '@client/components/Block/model'
import ColumnModel from '@client/components/DroppableWrapper/model'
import Settings from '@client/utils/Settings'
import {
  SECTION_BORDER_STYLES,
  SECTION_BORDER_RADIUS_STYLES_VALUES,
  ITEM_TYPE_SURVEY_THANK_YOU,
} from '@client/components/Editor/settings'

export const getSectionMaxWidth = (sectionModel) => {
  let maxWidth
  if (sectionModel.sectionType === SECTION_TYPES.ONECOL) {
    maxWidth = 642
  }

  if (sectionModel.sectionType === SECTION_TYPES.TWOCOL) {
    maxWidth = 321
  }

  if (sectionModel.sectionType === SECTION_TYPES.THREECOL) {
    maxWidth = 214
  }

  if (sectionModel.sectionType === SECTION_TYPES.PRODUCTSTHREECOL) {
    maxWidth = 214
  }

  if (sectionModel.sectionType === SECTION_TYPES.PRODUCTSTWOCOL) {
    maxWidth = 321
  }

  if (sectionModel.isBoxed) {
    if (sectionModel.sectionType === SECTION_TYPES.TWOCOL) {
      maxWidth = 311
    }

    if (sectionModel.sectionType === SECTION_TYPES.THREECOL) {
      maxWidth = 200
    }

    if (sectionModel.sectionType === SECTION_TYPES.PRODUCTSTHREECOL) {
      maxWidth = 200
    }

    if (sectionModel.sectionType === SECTION_TYPES.PRODUCTSTWOCOL) {
      maxWidth = 311
    }
  }
  return maxWidth || 642 // Default fallback to prevent NaN
}

export const getBorderMaxWidthReductionByColumn = (reduction, sectionModel) => {
  if (sectionModel.isFullWidth) {
    return reduction
  }
  if (sectionModel.isContentWidth) {
    if (sectionModel.sectionType === SECTION_TYPES.TWOCOL) {
      return Math.ceil(reduction / 2)
    }
    if (sectionModel.sectionType === SECTION_TYPES.THREECOL) {
      return Math.ceil(reduction / 3)
    }
    if (sectionModel.sectionType === SECTION_TYPES.PRODUCTSTHREECOL) {
      return Math.ceil(reduction / 3)
    }
    if (sectionModel.sectionType === SECTION_TYPES.PRODUCTSTWOCOL) {
      return Math.ceil(reduction / 2)
    }
  }
  return reduction
}

export const getBaseBorderMaxWidthReduction = (sectionModel) => {
  if (
    sectionModel.settings.values.borderStyle === SECTION_BORDER_STYLES[0].value
  ) {
    return 0
  }
  const borderWidth = sectionModel.settings.values.borderWidth || 0
  if (!sectionModel.settings.values.hasBorderSides) {
    return borderWidth * 2
  }
  if (
    sectionModel.settings.values.hasBorderSides &&
    (sectionModel.settings.values.borderSides || []).includes(
      SECTION_BORDER_STYLES[1].value
    ) &&
    (sectionModel.settings.values.borderSides || []).includes(
      SECTION_BORDER_STYLES[3].value
    )
  ) {
    return borderWidth * 2
  }
  if (
    sectionModel.settings.values.hasBorderSides &&
    ((sectionModel.settings.values.borderSides || []).includes(
      SECTION_BORDER_STYLES[1].value
    ) ||
      (sectionModel.settings.values.borderSides || []).includes(
        SECTION_BORDER_STYLES[3].value
      ))
  ) {
    return borderWidth
  }
  return 0
}

export const getBorderMaxWidthReduction = (sectionModel) => {
  if (sectionModel.isFullWidth) {
    return 0
  }
  if (sectionModel.isBoxed) {
    return 0
  }
  return getBaseBorderMaxWidthReduction(sectionModel)
}

export default class SectionModel extends BlockModel {
  constructor(data = {}, editor) {
    super(data)

    makeObservable(this)

    this.editor = editor

    this.restore(data)
  }

  @observable columns = []

  @observable sectionType = SECTION_TYPES.ONECOL

  @computed get allColors() {
    let columnsColors = [this.settings.values.backgroundColor]

    this.columns.forEach((item) => {
      if (item.allColors) {
        columnsColors = [...columnsColors, ...item.allColors]
      }
    })

    return Array.from(new Set(columnsColors.filter(Boolean)))
  }

  @computed get hasGutter() {
    return (
      this.columns.length > 1 &&
      this.settings.values.layout === SECTION_LAYOUTS[2].value
    )
  }

  @computed get isFullWidth() {
    return this.settings.values.layout === SECTION_LAYOUTS[0].value
  }

  @computed get isContentWidth() {
    return this.settings.values.layout === SECTION_LAYOUTS[1].value
  }

  @computed get isBoxed() {
    return this.settings.values.layout === SECTION_LAYOUTS[2].value
  }

  @computed get innerTableBackgroundImage() {
    const settings = this.settings.values
    const hasBackgroundImage =
      this.isContentWidth &&
      settings.hasBackgroundImage &&
      settings.backgroundImageUrl
    return hasBackgroundImage ? settings.backgroundImageUrl : ''
  }

  @computed get borderStyles() {
    const styles = {}
    const borderValue = `${this.settings.values.borderWidth}px ${this.settings.values.borderStyle} ${this.settings.values.borderColor}`
    ;(this.settings.values.hasBorderSides
      ? this.settings.values.borderSides || []
      : ['']
    ).forEach((side) => {
      styles[`border${upperFirst(side)}`] = borderValue
    })
    return styles
  }

  @computed get borderRadiusStyles() {
    const styles = {}
    const sides = this.settings.values.hasBorderRadiusSides
      ? SECTION_BORDER_RADIUS_STYLES_VALUES
      : ['']

    if (sides.length) {
      sides.forEach((side) => {
        const value =
          get(this.settings.values.borderRadiusSides, side) ||
          this.settings.values.borderRadius ||
          0
        if (!value) {
          return
        }
        styles[`border${upperFirst(side)}Radius`] = `${value}px`
      })
    }
    return styles
  }

  @computed get borderBoxMaxWidthReduction() {
    if (!this.isBoxed) {
      return 0
    }
    return getBaseBorderMaxWidthReduction(this)
  }

  @computed get borderMaxWidthReduction() {
    return getBorderMaxWidthReductionByColumn(
      getBorderMaxWidthReduction(this),
      this
    )
  }

  @computed get columnStyles() {
    const styles = {}

    styles.maxWidth = `${
      getSectionMaxWidth(this) - this.borderMaxWidthReduction
    }px`

    return styles
  }

  @action setProductSectionType = (sectionType) => {
    if (sectionType === SECTION_TYPES.PRODUCTSTHREECOL) {
      this.sectionType = SECTION_TYPES.PRODUCTSTHREECOL
    } else if (sectionType === SECTION_TYPES.PRODUCTSTWOCOL) {
      this.sectionType = SECTION_TYPES.PRODUCTSTWOCOL
    }

    const numberOfColumns = COLUMNS_COUNT[this.sectionType] || 0
    if (this.columns.length < numberOfColumns) {
      this.columns = Array.from({ length: numberOfColumns }, (v, i) => i).map(
        (i) =>
          !!this.columns[i] ? this.columns[i] : new ColumnModel({}, this.editor)
      )
    } else if (this.columns.length > numberOfColumns) {
      this.columns = this.columns.slice(0, numberOfColumns)
    }
  }

  getInnerTableStyles(settings) {
    const styles = {}
    if (
      this.sectionType === SECTION_TYPES.LANDINGTWOCOL ||
      this.sectionType === SECTION_TYPES.TWOCOL
    ) {
      styles.alignItems = this.settings.values.alignItems
    }
    const result = {
      ...styles,
      color: settings.values.normalText_color,
      fontSize: settings.values.normalText_fontSize + 'px',
      lineHeight: settings.values.normalText_lineHeight * 100 + '%',
      fontFamily: settings.values.normalText_fontFamily,
    }
    return result
  }

  serialize = () => {
    return {
      type: this.type,
      sectionType: this.sectionType,
      columns: this.columns.map((column) => column.serialize()),
      settings: this.settings.serialize(),
    }
  }

  restore = (data) => {
    this.type = 'SECTION'

    this.sectionType = data.sectionType || SECTION_TYPES.ONECOL

    const defaultSettings = {
      layout: SECTION_LAYOUTS[0].value,
      widthType: SECTION_WIDTH_TYPES[1].value,
      hasBottomSpacing: false,
      bottomSpacing: '20',
      backgroundColor: null,
      hasBackgroundImage: false,
      backgroundImageUrl: '',
      contentColor: null,
      stackColumnsOnMobile: true,
      borderColor: '#444444',
      borderRadiusSides: {},
      hasBorderRadiusSides: false,
      sidePadding: 20,
      middlePadding: 40,
    }

    if (
      this.sectionType === SECTION_TYPES.LANDINGONECOL ||
      this.sectionType === SECTION_TYPES.LANDINGTWOCOL ||
      get(this.editor, 'kind') === ITEM_TYPE_SURVEY_THANK_YOU
    ) {
      defaultSettings.layout = SECTION_LAYOUTS[0].value
      defaultSettings.sidePadding = 40
    }

    if (
      this.sectionType === SECTION_TYPES.FORMCOL ||
      this.sectionType === SECTION_TYPES.FORMSUCCESSCOL
    ) {
      defaultSettings.firstColumnWidthPersent = 50
      defaultSettings.bgImagePosition =
        TWOCOL_SECTION_BG_IMAGE_POSITION_TYPES[1].value
    }

    if (
      this.sectionType === SECTION_TYPES.TWOCOL ||
      this.sectionType === SECTION_TYPES.LANDINGTWOCOL
    ) {
      defaultSettings.alignItems =
        data.alignItems || SECTION_VERTICAL_ALIGN[0].value
      defaultSettings.firstColumnWidthPersent = 50
    }

    if (
      this.sectionType === SECTION_TYPES.PRODUCTSTHREECOL ||
      this.sectionType === SECTION_TYPES.PRODUCTSTWOCOL
    ) {
      defaultSettings.stackColumnsOnMobile = false
    }

    this.settings = new Settings(defaultSettings)
    this.settings.set(data.settings)

    if (data.columns && isArray(data.columns) && data.columns.length) {
      this.columns = data.columns.map(
        (column) => new ColumnModel(column, this.editor)
      )
    } else {
      this.columns = Array.from(
        { length: COLUMNS_COUNT[this.sectionType] || 0 },
        (v, i) => i
      ).map(() => {
        return new ColumnModel(data, this.editor)
      })
    }
  }
}

export const NULL = new SectionModel()
